import { useMemo, useState } from 'react'
import { useServices, useEventTimetable } from '../../hooks/useServices'
import { useOperatingDays } from '../../hooks/useScheduleV2'
import { useArchived } from '../../contexts/ArchiveContext'
import { parseIsoDate } from '../../lib/dates'
import { Icon } from '../Icon'
import { ServiceEditorV2 } from './ServiceEditorV2'
import { ImportWttModal } from './ImportWttModal'
import { AssignCrewModal } from './AssignCrewModal'
import { OperatingDayEditor } from './OperatingDayEditor'
import type { EventListItem, OperatingDay, Service, TimetableRow } from '../../types'

// ScheduleTabV2 — spec 14 + 15 v2 implementation. Same two-card layout as
// the v1 ScheduleTab, with the four spec-15 modals wired in:
//   - Row click → ServiceEditorV2 (edit)
//   - + Add Service → ServiceEditorV2 (create)
//   - Import WTT → ImportWttModal
//   - Assign button → AssignCrewModal
//   - Day cell click → OperatingDayEditor

interface Props {
  event: EventListItem
}

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export function ScheduleTabV2({ event }: Props) {
  const archived = useArchived()
  const { data: services, isLoading, error } = useServices(event.id)
  const { data: opDays = [] } = useOperatingDays(event.id)
  // Dual-source awareness (parity with the v1 ScheduleTab): when the event
  // is MERAC-bound (dbo.MeracEventMap), the timetable comes from MERAC
  // sessions and is read-only here — no import/add/edit/crew.
  const { data: timetable } = useEventTimetable(event.id)
  const isMerac = timetable?.source === 'merac'
  const meracRows: TimetableRow[] = isMerac ? (timetable?.services ?? []) : []

  const [editing, setEditing] = useState<Service | null>(null)
  const [creating, setCreating] = useState(false)
  const [importing, setImporting] = useState(false)
  const [assigning, setAssigning] = useState<Service | null>(null)
  const [editingDay, setEditingDay] = useState<string | null>(null)

  // Day columns = the event's actual date set (item 7), so non-consecutive
  // multi-date events show only their real days, not every day in the envelope.
  const dates = useMemo(() => {
    const iso = event.dates && event.dates.length > 0 ? [...event.dates].sort() : [event.startDate]
    return iso.map(parseIsoDate)
  }, [event.dates, event.startDate])

  if (isLoading) {
    return <div className="text-ink-soft text-sm font-mono">Loading services…</div>
  }
  if (error) {
    return (
      <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
        <div className="font-semibold mb-1">Could not load services</div>
        <pre className="text-xs whitespace-pre-wrap">{(error as Error).message}</pre>
      </div>
    )
  }

  const list = services ?? []
  const opDayByDate = new Map<string, OperatingDay>(opDays.map((d) => [d.date, d]))

  return (
    <div className="space-y-5">
      <div className="flex items-baseline gap-3">
        <h2 className="font-display text-xl">Schedule</h2>
        <span
          className="text-xs uppercase tracking-wide font-semibold text-green border border-green/50 px-2 py-0.5 rounded"
          title="Schedule v2 — enabled via ?v2=schedule"
        >
          v2
        </span>
      </div>

      {/* MERAC source banner */}
      {isMerac && (
        <div className="bg-parchment border border-brass rounded p-3 flex items-start gap-3">
          <Icon name="train" size={16} />
          <div className="text-sm">
            <div className="font-semibold">MERAC-sourced timetable</div>
            <div className="text-xs text-ink-soft mt-0.5">
              This event is linked to MERAC event code{' '}
              <span className="font-mono font-semibold">{timetable?.meracEventId}</span>.
              Sessions, capacity and bookings update automatically as the MERAC sync runs — no manual entry needed.
            </div>
          </div>
        </div>
      )}

      {/* Services table */}
      <div className="bg-white border border-line rounded">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-line">
          <div className="flex-1">
            <div className="text-sm font-semibold">Train Services — Daily Timetable</div>
            <div className="text-xs text-ink-soft mt-0.5">
              {isMerac
                ? 'Sourced live from MERAC ticketing — read-only.'
                : 'Each service operates Pickering → Newbridge return. Click "Assign" to set carriage stewards. Capacity is auto-pulled from the working timetable.'}
            </div>
          </div>
          {!archived && !isMerac && (
            <>
              <button
                type="button"
                onClick={() => setImporting(true)}
                className="text-xs font-semibold text-ink border border-line rounded px-3 py-1.5 hover:bg-parchment"
              >
                Import WTT
              </button>
              <button
                type="button"
                onClick={() => setCreating(true)}
                className="text-xs font-semibold text-parchment bg-green hover:bg-green/90 rounded px-3 py-1.5 transition-colors"
              >
                + Add Service
              </button>
            </>
          )}
        </div>

        {isMerac ? (
          meracRows.length === 0 ? (
            <div className="px-4 py-10 text-center">
              <div className="font-semibold mb-1">No MERAC sessions yet</div>
              <div className="text-sm text-ink-soft">
                Sessions appear here automatically once the MERAC sync delivers them for this event.
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-ink-soft border-b border-line">
                    <th className="px-4 py-2 font-semibold w-24">Date</th>
                    <th className="px-4 py-2 font-semibold w-20">Time</th>
                    <th className="px-4 py-2 font-semibold">Session</th>
                    <th className="px-4 py-2 font-semibold">From → To</th>
                    <th className="px-4 py-2 font-semibold w-20 text-right">Capacity</th>
                    <th className="px-4 py-2 font-semibold w-48">Booking</th>
                  </tr>
                </thead>
                <tbody>
                  {meracRows.map((r) => (
                    <MeracServiceRow key={r.id} row={r} />
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : list.length === 0 ? (
          <EmptyServices
            archived={archived}
            onAdd={() => setCreating(true)}
            onImport={() => setImporting(true)}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-ink-soft border-b border-line">
                  <th className="px-4 py-2 font-semibold w-20">Time</th>
                  <th className="px-4 py-2 font-semibold">Service</th>
                  <th className="px-4 py-2 font-semibold">Loco</th>
                  <th className="px-4 py-2 font-semibold">From → To</th>
                  <th className="px-4 py-2 font-semibold w-20 text-right">Capacity</th>
                  <th className="px-4 py-2 font-semibold w-48">Booking</th>
                  <th className="px-4 py-2 font-semibold w-24">Crew</th>
                </tr>
              </thead>
              <tbody>
                {list.map((s) => (
                  <ServiceRow
                    key={s.id}
                    service={s}
                    archived={archived}
                    onEdit={() => setEditing(s)}
                    onAssign={() => setAssigning(s)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Operating days */}
      <div className="bg-white border border-line rounded">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-line">
          <div className="text-sm font-semibold flex-1">Operating Days</div>
          <span className="text-xs text-ink-soft font-mono">
            {dates.length} day{dates.length === 1 ? '' : 's'} total
          </span>
        </div>
        <div className="px-4 py-4">
          {dates.length === 0 ? (
            <p className="text-xs italic text-ink-soft">No operating days — check the event start/end dates.</p>
          ) : (
            <div
              className="grid gap-2.5"
              style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))' }}
            >
              {dates.map((d, i) => {
                const iso = d.toISOString().slice(0, 10)
                const opDay = opDayByDate.get(iso)
                // Default: v5 fallback (Thursdays off after the 5th day) when
                // no OperatingDay row exists, so seeded events match v5 visually.
                const fallbackOff = d.getUTCDay() === 4 && i > 4
                const isOff = opDay ? !opDay.running : fallbackOff
                const svcCount = opDay?.serviceCount
                  ?? (isMerac ? meracRows.filter((r) => r.date === iso).length : list.length)
                return (
                  <DayCard
                    key={i}
                    date={d}
                    iso={iso}
                    isOff={isOff}
                    serviceCount={svcCount}
                    note={opDay?.note ?? null}
                    onClick={archived ? undefined : () => setEditingDay(iso)}
                  />
                )
              })}
            </div>
          )}
        </div>
      </div>

      {!archived && !isMerac && (creating || editing) && (
        <ServiceEditorV2
          event={event}
          service={editing}
          services={list}
          onClose={() => {
            setEditing(null)
            setCreating(false)
          }}
        />
      )}
      {!archived && !isMerac && importing && (
        <ImportWttModal eventId={event.id} existing={list} onClose={() => setImporting(false)} />
      )}
      {!archived && !isMerac && assigning && (
        <AssignCrewModal service={assigning} onClose={() => setAssigning(null)} />
      )}
      {!archived && editingDay && (
        <OperatingDayEditor
          eventId={event.id}
          date={editingDay}
          existing={opDayByDate.get(editingDay)}
          services={list}
          onClose={() => setEditingDay(null)}
        />
      )}
    </div>
  )
}

// ---- Service row -----------------------------------------------------------

function ServiceRow({
  service,
  archived,
  onEdit,
  onAssign
}: {
  service: Service
  archived: boolean
  onEdit: () => void
  onAssign: () => void
}) {
  const capacity = service.capacity ?? 0
  const booked = service.booked ?? 0
  const pct = capacity === 0 ? 0 : (booked / capacity) * 100
  const fillColour =
    pct > 95 ? 'bg-oxblood' : pct > 80 ? 'bg-brass' : 'bg-green'
  const route =
    service.originStation && service.destinationStation
      ? `${service.originStation} → ${service.destinationStation}`
      : service.originStation || service.destinationStation || '—'
  return (
    <tr
      onClick={archived ? undefined : onEdit}
      className={
        'border-b border-line last:border-b-0 transition-colors ' +
        (archived ? '' : 'hover:bg-parchment cursor-pointer')
      }
    >
      <td className="px-4 py-3 font-mono font-semibold">{service.timeText}</td>
      <td className="px-4 py-3 font-semibold">{service.serviceLabel}</td>
      <td className="px-4 py-3">
        <span className="inline-flex items-center gap-1">
          <Icon name="train" size={13} />
          {service.loco ?? <span className="italic text-ink-soft">—</span>}
        </span>
      </td>
      <td className="px-4 py-3 text-ink-soft">{route}</td>
      <td className="px-4 py-3 text-right font-mono">
        {capacity || <span className="italic text-ink-soft">—</span>}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="flex-1 h-2 bg-parchment border border-line rounded-full overflow-hidden">
            <div
              className={`h-full ${fillColour} transition-all`}
              style={{ width: `${Math.min(pct, 100)}%` }}
            />
          </div>
          <span className="text-xs font-mono font-semibold min-w-[60px] text-right">
            {booked}/{capacity}
          </span>
        </div>
      </td>
      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
        {!archived && (
          <button
            type="button"
            onClick={onAssign}
            className="text-xs font-semibold text-ink border border-line rounded px-2 py-1 hover:bg-parchment"
          >
            Assign
          </button>
        )}
      </td>
    </tr>
  )
}

// ---- MERAC row (read-only) ---------------------------------------------------

function MeracServiceRow({ row }: { row: TimetableRow }) {
  const capacity = row.capacity ?? 0
  const booked = row.meracBooked ?? 0
  const pct = capacity === 0 ? 0 : (booked / capacity) * 100
  const fillColour = pct > 95 ? 'bg-oxblood' : pct > 80 ? 'bg-brass' : 'bg-green'
  const route =
    row.originStation && row.destinationStation
      ? `${row.originStation} → ${row.destinationStation}`
      : row.originStation || row.destinationStation || '—'
  const dateText = row.date
    ? `${WEEKDAY_SHORT[parseIsoDate(row.date).getUTCDay()]} ${parseIsoDate(row.date).getUTCDate()} ${MONTH_SHORT[parseIsoDate(row.date).getUTCMonth()]}`
    : '—'
  return (
    <tr className="border-b border-line last:border-b-0">
      <td className="px-4 py-3 text-ink-soft text-xs font-semibold whitespace-nowrap">{dateText}</td>
      <td className="px-4 py-3 font-mono font-semibold">{row.timeText}</td>
      <td className="px-4 py-3 font-semibold">{row.serviceLabel}</td>
      <td className="px-4 py-3 text-ink-soft">{route}</td>
      <td className="px-4 py-3 text-right font-mono">
        {capacity || <span className="italic text-ink-soft">—</span>}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="flex-1 h-2 bg-parchment border border-line rounded-full overflow-hidden">
            <div className={`h-full ${fillColour} transition-all`} style={{ width: `${Math.min(pct, 100)}%` }} />
          </div>
          <span className="text-xs font-mono font-semibold min-w-[60px] text-right">
            {booked}/{capacity || '—'}
          </span>
        </div>
      </td>
    </tr>
  )
}

// ---- Empty + day card ------------------------------------------------------

function EmptyServices({
  archived,
  onAdd,
  onImport
}: {
  archived: boolean
  onAdd: () => void
  onImport: () => void
}) {
  return (
    <div className="px-4 py-10 text-center">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-parchment text-ink-soft text-xl mb-3">
        <Icon name="train" size={20} />
      </div>
      <div className="font-semibold mb-1">No services scheduled yet</div>
      <div className="text-sm text-ink-soft mb-3">
        Pull in the working timetable for this event, or add services one at a time.
      </div>
      {!archived && (
        <div className="flex gap-2 justify-center">
          <button
            type="button"
            onClick={onImport}
            className="text-xs font-semibold text-parchment bg-green hover:bg-green/90 rounded px-3 py-1.5"
          >
            Import WTT
          </button>
          <button
            type="button"
            onClick={onAdd}
            className="text-xs font-semibold text-ink border border-line rounded px-3 py-1.5 hover:bg-parchment"
          >
            + Add Service
          </button>
        </div>
      )}
    </div>
  )
}

function DayCard({
  date,
  iso: _iso,
  isOff,
  serviceCount,
  note,
  onClick
}: {
  date: Date
  iso: string
  isOff: boolean
  serviceCount: number
  note: string | null
  onClick: (() => void) | undefined
}) {
  const weekday = WEEKDAY_SHORT[date.getUTCDay()]
  const day = date.getUTCDate()
  const month = MONTH_SHORT[date.getUTCMonth()]
  const interactive = onClick != null
  return (
    <div
      onClick={onClick}
      className={`border border-line rounded p-2.5 text-center transition-colors ${
        interactive ? 'cursor-pointer hover:border-green' : ''
      } ${isOff ? 'bg-oxblood-soft' : 'bg-parchment'}`}
    >
      <div className="text-[10px] uppercase tracking-wide text-ink-soft">{weekday}</div>
      <div className="font-display text-xl font-bold leading-tight">{day}</div>
      <div className="text-xs text-ink-soft">{month}</div>
      {isOff ? (
        <div className="text-xs font-semibold text-oxblood mt-1">Non-running</div>
      ) : (
        <div className="text-xs font-semibold text-green mt-1">
          {serviceCount} service{serviceCount === 1 ? '' : 's'}
        </div>
      )}
      {note && (
        <div className="text-[10px] italic text-ink-soft mt-1 truncate" title={note}>
          {note}
        </div>
      )}
    </div>
  )
}
